import { Editor } from '@ckeditor/ckeditor5-core/src/editor';
import { ConnectedRouter } from 'connected-react-router';
import React from 'react';
import ReactDOM from 'react-dom';
import { IntlProvider } from 'react-intl';
import { QueryClientProvider, QueryClient } from 'react-query';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { Document } from 'api/document/types/document';
import { getCkEditorUploadConfig } from 'components/organisms/ck-collab-editor/helpers';
import { store, history } from 'data';
import {
  selectSections,
  updateDocumentTemplateContentAction,
} from 'data/templates/documents';
import { useDocumentById } from 'data/templates/documents/hooks/use-document-by-id';
import translations from 'translations/en.json';
import { TemplateTaskSection } from './resource-section';

const queryClient = new QueryClient();
const taskSectionRenderer = (
  resourceSectionId: number,
  container: HTMLElement,
  sectionId: number,
  docId: number,
  readOnly?: boolean,
): void => {
  ReactDOM.render(
    <Provider store={store}>
      <IntlProvider locale="en" messages={translations}>
        <ConnectedRouter history={history}>
          <QueryClientProvider client={queryClient}>
            <TemplateTaskSection
              docId={docId}
              resourceSectionId={resourceSectionId}
              sectionId={sectionId}
              readOnly={readOnly}
            />
          </QueryClientProvider>
        </ConnectedRouter>
      </IntlProvider>
    </Provider>,
    container,
  );
};

export const useEditorConfigs = ({
  docId,
  readOnly,
}: {
  docId: number;
  readOnly?: boolean;
}): {
  apiService: {
    getDocument: () => Promise<{
      document: AtLeast<Document, 'title' | 'documentId' | 'sections'>;
    }>;
  };
  extraConfig: {
    taskSection: {
      renderer: (
        resourceSectionId: number,
        container: HTMLElement,
        sectionId: DOMStringMap,
      ) => void;
    };
    autosave: {
      waitingTime: number;
      save(editor: Editor): Promise<void>;
    };
    simpleUpload: {
      uploadUrl: string;
      withCredentials: boolean;
      headers: {
        'api-key': string;
      };
    };
  };
} => {
  const dispatch = useDispatch();
  const documentTemplate = useDocumentById(docId);
  const sections = useSelector(
    selectSections(
      documentTemplate?.templateId as number,
      documentTemplate?.templateContent?.sections as unknown[] as number[],
    ),
  );

  const apiService = {
    getDocument: () => {
      if (documentTemplate) {
        const document = {
          ...documentTemplate.templateContent,
          sections,
        } as AtLeast<Document, 'title' | 'documentId' | 'sections'>;

        return Promise.resolve({
          document,
        });
      }

      return Promise.reject();
    },
  };

  const extraConfig = {
    taskSection: {
      renderer: (
        resourceSectionId: number,
        container: HTMLElement,
        section: DOMStringMap,
      ) => {
        return taskSectionRenderer(
          resourceSectionId,
          container,
          parseInt(section.sectionid || '', 10),
          docId,
          readOnly,
        );
      },
    },
    autosave: {
      waitingTime: 2000,
      save(editor: Editor) {
        const { sectionid } = editor.sourceElement.dataset;
        const content = editor.getData();

        if (sectionid && documentTemplate && content.length > 0) {
          dispatch(
            updateDocumentTemplateContentAction({
              content,
              sectionId: parseInt(sectionid, 10),
              templateId: documentTemplate.templateId as number,
            }),
          );
        }

        return Promise.resolve();
      },
    },
    ...getCkEditorUploadConfig(),
  };

  return {
    apiService,
    extraConfig,
  };
};
